import React, { useRef, useState } from "react";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import ClaudeIcon from "@/assets/images/claude.svg?react";
import CodexIcon from "@/assets/images/codex.svg?react";
import CursorIcon from "@/assets/images/cursor.svg?react";
import GeminiIcon from "@/assets/images/gemini.svg?react";
import VscodeIcon from "@/assets/images/vscode.svg?react";
import { Button } from "@/components/common/Button";
import { Switch } from "@/components/common/Switch";
import { Fold } from "@/components/common/Fold";
import { TooltipButton } from "@/components/common/TooltipButton";
import { TruncatedText } from "@/components/common/TruncatedText";
import { CodeBlock } from "@/components/common/CodeBlock";
import { SectionRule } from "@/components/common/SectionRule";
import { showNotificationGlobal } from "@/stores/useNotificationStore";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import type { Org } from "@/lib/api/organizations";
import {
  claudeCodeCmd,
  claudeDesktopEntry,
  codexCmd,
  cursorDeepLink,
  geminiCmd,
  isLocalEnv,
  mcpServerEntry,
  mcpServerUrl,
  type OrgLock,
  vscodeDeepLink,
  vscodeServerEntry,
} from "./mcpConnect";

const LOGO = { width: 18, height: 18, "aria-hidden": true } as const;
/// Not a tile: the AI clients header's action opens its setup.
const OTHER_CLIENT = "Other MCP client";

interface Snippet {
  code: string;
  label: string;
}
interface Step {
  text?: React.ReactNode;
  snippet?: Snippet;
  button?: { label: string; link: string };
}
interface HowTo {
  steps: Step[];
  note?: React.ReactNode;
  manual?: Snippet;
}
interface Client extends HowTo {
  name: string;
  sub?: string;
  logo: React.ReactNode;
  /// Local goes through the `mcp-remote` bridge, which no one-click install or
  /// hosted connector can reach, so these steps paste config instead.
  local?: HowTo;
}

const B: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <b className="font-semibold">{children}</b>
);

const clients = (lock: OrgLock): Client[] => {
  const url = mcpServerUrl(lock?.org_id);
  const APPROVE: Step = {
    text: lock
      ? "Approve access in the browser."
      : "Approve access in the browser and pick the organization.",
  };
  const link: Step = {
    text: "Copy the link.",
    snippet: { code: url, label: "Server URL" },
  };
  const entry: Snippet = {
    code: mcpServerEntry(lock),
    label: "JSON: mcpServers entry for MyCorr",
  };
  const vsEntry: Snippet = {
    code: vscodeServerEntry(lock),
    label: "JSON: VS Code servers entry for MyCorr",
  };
  return [
    {
      name: "Claude",
      sub: "Web, desktop, mobile",
      logo: <ClaudeIcon {...LOGO} />,
      note: (
        <>
          On Team or Enterprise, an Owner adds it once under{" "}
          <B>Organization settings → Connectors</B>, and each member connects
          with their own account.
        </>
      ),
      steps: [
        link,
        {
          text: (
            <>
              In Claude, go to{" "}
              <B>Customize → Connectors → Add custom connector</B> and paste it.
            </>
          ),
        },
        APPROVE,
      ],
      local: {
        steps: [
          {
            text: (
              <>
                In Claude Desktop, go to{" "}
                <B>Settings → Developer → Edit Config</B>, paste this inside{" "}
                <code>mcpServers</code>, then relaunch.
              </>
            ),
            snippet: {
              code: claudeDesktopEntry(lock),
              label: "JSON: Claude Desktop mcpServers entry for MyCorr",
            },
          },
          APPROVE,
        ],
      },
    },
    {
      name: "Claude Code",
      sub: "Terminal command",
      logo: <ClaudeIcon {...LOGO} />,
      steps: [
        {
          text: "Run in your terminal.",
          snippet: {
            code: claudeCodeCmd(lock),
            label: "Shell: claude mcp add for MyCorr",
          },
        },
        APPROVE,
      ],
    },
    {
      name: "ChatGPT",
      sub: "Paste a link",
      logo: <CodexIcon {...LOGO} />,
      steps: [
        link,
        {
          text: (
            <>
              In ChatGPT, open <B>Plugins</B>, select <B>+</B>, then{" "}
              <B>Add custom MCP server</B> and paste it.
            </>
          ),
        },
        APPROVE,
      ],
      note: "On Business and Enterprise, a workspace admin adds it for the workspace.",
      local: { steps: [], note: "ChatGPT can't reach localhost." },
    },
    {
      name: "Cursor",
      sub: "One-click install",
      logo: <CursorIcon {...LOGO} />,
      steps: [
        {
          button: {
            label: "Add to Cursor",
            link: cursorDeepLink(lock),
          },
        },
        APPROVE,
      ],
      manual: entry,
      local: {
        steps: [
          {
            text: (
              <>
                Paste this inside <code>mcpServers</code> in Cursor's MCP
                settings.
              </>
            ),
            snippet: entry,
          },
          APPROVE,
        ],
      },
    },
    {
      name: "VS Code",
      sub: "GitHub Copilot",
      logo: <VscodeIcon {...LOGO} />,
      steps: [
        {
          button: {
            label: "Install in VS Code",
            link: vscodeDeepLink(lock),
          },
        },
        APPROVE,
      ],
      manual: vsEntry,
      local: {
        steps: [
          {
            text: (
              <>
                Paste this inside <code>servers</code> in{" "}
                <code>.vscode/mcp.json</code>.
              </>
            ),
            snippet: vsEntry,
          },
          APPROVE,
        ],
      },
    },
    {
      name: "Codex",
      sub: "Terminal command",
      logo: <CodexIcon {...LOGO} />,
      steps: [
        {
          text: "Run in your terminal.",
          snippet: {
            code: codexCmd(lock),
            label: "Shell: codex mcp add for MyCorr",
          },
        },
        APPROVE,
      ],
    },
    {
      name: "Gemini CLI",
      sub: "Terminal command",
      logo: <GeminiIcon {...LOGO} />,
      steps: [
        {
          text: "Run in your terminal.",
          snippet: {
            code: geminiCmd(lock),
            label: "Shell: gemini mcp add for MyCorr",
          },
        },
        APPROVE,
      ],
    },
    {
      name: OTHER_CLIENT,
      logo: <MoreHorizIcon sx={{ fontSize: 18 }} />,
      steps: [link, { text: "Add it as a remote MCP server." }, APPROVE],
      manual: entry,
      local: {
        steps: [
          {
            text: (
              <>
                Paste this inside your client's <code>mcpServers</code>.
              </>
            ),
            snippet: entry,
          },
          APPROVE,
        ],
      },
    },
  ];
};

function launchDeepLink(url: string, appLabel: string) {
  let opened = false;
  const markOpened = () => {
    opened = true;
  };
  window.addEventListener("blur", markOpened, { once: true });

  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  iframe.src = url;
  document.body.appendChild(iframe);

  window.setTimeout(() => {
    iframe.remove();
    window.removeEventListener("blur", markOpened);
    if (!opened && !document.hidden) {
      showNotificationGlobal(
        `If ${appLabel} didn't open, make sure it's installed — or pick "${OTHER_CLIENT}" to paste the server URL.`,
        "default",
      );
    }
  }, 1500);
}

// The name the page's OrgBadge shows for the active org.
const orgLabel = (org: Org) => (org.is_personal ? "Personal" : org.name);

const codeBox = ({ code, label }: Snippet) => (
  <CodeBlock
    code={code}
    ariaLabel={label}
    onCopy={() => showNotificationGlobal("Copied", "success")}
    className="bg-bg! border-border select-text"
  />
);

export const AccessConnectPanel: React.FC = () => {
  const [open, setOpen] = useState<string | null>(null);
  // The panel keeps showing the last client while it folds shut.
  const [shown, setShown] = useState<string | null>(null);
  if (open && open !== shown) setShown(open);
  const [manualOpen, setManualOpen] = useState(false);
  const cards = useRef<Record<string, HTMLButtonElement | null>>({});
  const activeOrg = useOrgContextStore(
    (s) => s.orgs.find((o) => o.org_id === s.activeOrgId) ?? null,
  );
  const [pinned, setPinned] = useState(false);
  const lock = pinned ? activeOrg : null;

  const list = clients(lock);
  const tiles = list.filter((c) => c.name !== OTHER_CLIENT);
  const client = list.find((c) => c.name === shown);
  const howTo = isLocalEnv() && client?.local ? client.local : client;
  const close = () => {
    if (open) cards.current[open]?.focus();
    setOpen(null);
  };

  return (
    <section>
      <SectionRule
        inset
        icon={<AutoAwesomeOutlinedIcon sx={{ fontSize: 14 }} />}
        label="AI clients"
        action={{
          label: OTHER_CLIENT,
          expanded: open === OTHER_CLIENT,
          controls: "ai-client-howto",
          onClick: () => setOpen(open === OTHER_CLIENT ? null : OTHER_CLIENT),
          ref: (el) => {
            cards.current[OTHER_CLIENT] = el;
          },
        }}
      />
      <div>
        {/* `@` variants measure MainLayout's main pane: 3, 2, then 1 column. */}
        <div
          className="grid grid-cols-1 @sm:grid-cols-2 @2xl:grid-cols-3"
          style={{ gap: "var(--spacing-sm)" }}
          role="group"
          aria-label="AI clients"
        >
          {tiles.map((c) => {
            const expanded = open === c.name;
            return (
              // eslint-disable-next-line no-restricted-syntax -- a disclosure tile, not a Button-styled control
              <button
                key={c.name}
                type="button"
                ref={(el) => {
                  cards.current[c.name] = el;
                }}
                aria-expanded={expanded}
                aria-controls="ai-client-howto"
                className={`flex min-w-0 items-center gap-2 p-2 text-left rounded-app cursor-pointer ${
                  expanded ? "bg-list-selected-light" : "hover:bg-bg-hover"
                }`}
                onClick={() => setOpen(expanded ? null : c.name)}
              >
                <span className="grid shrink-0 place-items-center w-4.5 h-4.5 text-fg">
                  {c.logo}
                </span>
                <span className="flex min-w-0 flex-col">
                  <span className="text-fg-medium">{c.name}</span>
                  <TruncatedText className="text-fg-muted-regular-small">
                    {c.sub}
                  </TruncatedText>
                </span>
              </button>
            );
          })}
        </div>

        <Fold open={open !== null}>
          {client && howTo && (
            <div className="pt-1">
              <div
                id="ai-client-howto"
                className="flex flex-col gap-3 border border-border rounded-app bg-bg-subtle p-3"
              >
                <div className="flex items-center gap-2 text-fg-semibold">
                  <span className="grid place-items-center w-4.5 h-4.5">
                    {client.logo}
                  </span>
                  {client.name}
                  <span className="ml-auto">
                    <TooltipButton
                      title="Close"
                      aria-label="Close"
                      variant="default"
                      size="sm"
                      onClick={close}
                    >
                      <CloseIcon />
                    </TooltipButton>
                  </span>
                </div>
                {howTo.steps.length > 0 && activeOrg && (
                  <div className="flex flex-col gap-1">
                    <label className="flex items-center gap-2 self-start cursor-pointer">
                      <Switch
                        checked={pinned}
                        onChange={setPinned}
                        aria-label={`Pin to ${orgLabel(activeOrg)}`}
                      />
                      <span className="text-fg-regular">
                        Pin to {orgLabel(activeOrg)}
                      </span>
                    </label>
                    <p className="text-fg-muted-regular m-0">
                      {pinned
                        ? `Reaches ${orgLabel(activeOrg)} only: for a team connector pinned to it, or a second organization in the same client.`
                        : "One link for everyone: each person picks their organization when they approve."}
                    </p>
                  </div>
                )}
                {howTo.note && (
                  <p className="text-fg-muted-regular m-0">{howTo.note}</p>
                )}
                {howTo.steps.length > 0 && (
                  <ol className="flex flex-col gap-3 list-none m-0 p-0">
                    {howTo.steps.map((step, i) => (
                      <li
                        key={i}
                        className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-2 items-start"
                      >
                        <span className="grid place-items-center w-6 h-6 rounded-full bg-bg-hover text-mono-small font-medium">
                          {i + 1}
                        </span>
                        <div className="flex flex-col gap-2 pt-1 min-w-0">
                          {step.text && <span>{step.text}</span>}
                          {step.snippet && codeBox(step.snippet)}
                          {step.button && (
                            <Button
                              variant="primary"
                              size="sm"
                              className="self-start"
                              onClick={() =>
                                launchDeepLink(step.button!.link, client.name)
                              }
                            >
                              {step.button.label}
                            </Button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
                {howTo.manual && (
                  <div>
                    <Button
                      variant="quiet-link"
                      size="xs"
                      aria-expanded={manualOpen}
                      onClick={() => setManualOpen((v) => !v)}
                    >
                      <ChevronRightIcon
                        className="icon-sm"
                        style={{
                          rotate: manualOpen ? "90deg" : "0deg",
                          transition:
                            "rotate var(--duration-short) var(--ease-move)",
                        }}
                      />
                      Manual setup
                    </Button>
                    <Fold open={manualOpen}>
                      <div className="pt-2">{codeBox(howTo.manual)}</div>
                    </Fold>
                  </div>
                )}
              </div>
            </div>
          )}
        </Fold>
      </div>
    </section>
  );
};
